import { useCallback, useEffect, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner, Loading, ScreenRoot, ToastHost, useToasts } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import { Menu } from "../../../components/ui/Menu";
import { PERIODS, useOrgApi } from "../api/orgApi";
import type { OrgCourseRow, OrgReport, Period } from "../types";
import "../../../styles/teacher.css";
import "../../../styles/console.css";
import "../../../styles/org.css";

/** 0..1 → '78%' */
export const pct = (r: number) => `${Math.round(r * 100)}%`;

/**
 * FS-15-06 기관 비식별 집계(기관 담당자 · 운영자 · SU/공통 · SCR v0.6 sfs-15-06) — /org/report.
 *  - 상단 바 '🏫 Field · 기관 리포트' + 칩 'org-A · 이번 달'(기관 키 · 기간) · '비식별'
 *  - 타일 4: 코스 · 마무리 활동 실행률 · 작품 올린 비율 · 모드 완료율(PRD 성공 지표와 같은 정의 — 서버 집계)
 *  - '코스별 집계' 표: 코스(가명 — LTI 컨텍스트 ID) · 학생 수 · 활동 실행 n/m · 작품 올림 n/m · 모드 완료율 · 재시도율
 *    · 룸 안정성(로컬 전환 n / 중단 0) + 기간(이번 달 · 이번 학기 · 전체) · CSV 내보내기(같은 표 — text/csv 내려받기)
 *  - 학생 이름·별명·개별 결과·순위는 정의상 없다(SR-ACH-01 · PDM IR-01) — 코스 단위 집계만(org_key 경계 · SR-AUTH-03).
 */
export function OrgReportScreen() {
  const t = useT();
  const api = useOrgApi();
  const toasts = useToasts();
  const [period, setPeriod] = useState<Period>("month");
  const [report, setReport] = useState<OrgReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!api) return;
    let alive = true;
    setLoading(true);
    api.report(period).then(
      (r) => {
        if (!alive) return;
        setReport(r);
        setError(null);
        setLoading(false);
      },
      (e) => {
        if (!alive) return;
        setError(toApiError(e).code);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [api, period]);

  const periodName = t(`org.period.${period}`);
  useTopBar({
    icon: "🏫",
    title: t("org.topTitle"),
    chips: [
      ...(report?.orgKey
        ? [
            {
              label: t("org.chipOrg", { org: report.orgKey, period: periodName }),
              testId: "org-chip",
            },
          ]
        : []),
      { label: t("org.chipAnon"), testId: "org-anon-chip" },
    ],
  });

  const exportCsv = useCallback(async () => {
    if (!api) return;
    setExporting(true);
    try {
      const blob = await api.csv(period);
      download(blob, `field-org-report-${period}.csv`);
    } catch (e) {
      toasts.push("error", t("org.csvFailed"), toApiError(e).code);
    } finally {
      setExporting(false);
    }
  }, [api, period, toasts, t]);

  const tiles = report?.tiles;
  const tileList: [string, string][] = tiles
    ? [
        ["courses", String(tiles.courses)],
        ["activityRunRate", pct(tiles.activityRunRate)],
        ["uploadRate", pct(tiles.uploadRate)],
        ["modeCompletion", pct(tiles.modeCompletion)],
      ]
    : [];

  return (
    <ScreenRoot id="FS-15-06" title={t("org.title")} hideTitle className="pf-org">
      {error && <ErrorBanner cause={t("org.loadFailed")} action={t("org.loadNext")} code={error} />}
      <div className="pf-kpis pf-org__tiles" data-testid="org-tiles" aria-busy={loading}>
        {(tileList.length
          ? tileList
          : (["courses", "activityRunRate", "uploadRate", "modeCompletion"] as const).map(
              (k): [string, string] => [k, "—"],
            )
        ).map(([k, v]) => (
          <div key={k} className="pf-kpi" data-tile={k}>
            <span className="pf-muted">{t(`org.tile.${k}`)}</span>
            <b>{v}</b>
          </div>
        ))}
      </div>
      <section className="pf-pane pf-org__pane" aria-labelledby="org-courses">
        <div className="pf-row pf-org__head">
          <h2 id="org-courses" className="pf-h3">
            {t("org.courses")}
          </h2>
          <span className="pf-rc__label" aria-hidden="true">
            {t("org.periodLabel")}
          </span>
          <Menu
            testId="org-period"
            ariaLabel={t("org.periodLabel")}
            disabled={loading}
            label={<>{periodName} ▾</>}
            items={PERIODS.map((p) => ({ id: p, on: p === period, label: t(`org.period.${p}`) }))}
            onPick={(id) => setPeriod(id as Period)}
          />
          <Button
            variant="ghost"
            className="pf-btn--sm pf-row__end"
            disabled={!report || exporting}
            data-testid="org-csv"
            onClick={() => void exportCsv()}
          >
            {t("org.csv")}
          </Button>
        </div>
        {!report ? (
          !error && <Loading />
        ) : report.courses.length === 0 ? (
          <p className="pf-muted" data-testid="org-empty">
            {t("org.empty")}
          </p>
        ) : (
          <div className="pf-tbl-scroll pf-org__scroll" tabIndex={0} aria-labelledby="org-courses">
            <table className="pf-tbl pf-org__table" data-testid="org-table">
              <thead>
                <tr>
                  <th scope="col">{t("org.col.course")}</th>
                  <th scope="col">{t("org.col.students")}</th>
                  <th scope="col">{t("org.col.activities")}</th>
                  <th scope="col">{t("org.col.uploaded")}</th>
                  <th scope="col">{t("org.col.modeCompletion")}</th>
                  <th scope="col">{t("org.col.retryRate")}</th>
                  <th scope="col">{t("org.col.stability")}</th>
                </tr>
              </thead>
              <tbody>
                {report.courses.map((c) => (
                  <CourseRow key={c.course} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pf-banner pf-banner--info" role="note" data-testid="org-note">
          {t("org.note")}
        </p>
      </section>
      <ToastHost items={toasts.items} onDismiss={toasts.dismiss} pico={false} />
    </ScreenRoot>
  );
}

function CourseRow({ c }: { c: OrgCourseRow }) {
  const t = useT();
  const grade = c.grade ? (/^\d+$/.test(c.grade) ? t("org.grade", { n: c.grade }) : c.grade) : null;
  return (
    <tr data-course={c.course}>
      <th scope="row" className="pf-org__course">
        {grade ? t("org.courseWithGrade", { course: c.course, grade }) : c.course}
      </th>
      <td>{c.students}</td>
      <td>{t("org.ratio", { n: c.activitiesRun, m: c.activitiesTotal })}</td>
      <td>{t("org.ratio", { n: c.uploaded, m: c.students })}</td>
      <td>{pct(c.modeCompletion)}</td>
      <td>{pct(c.retryRate)}</td>
      <td>
        <span
          className={`pf-chip-static ${c.localFallbacks > 0 ? "pf-chip-static--run" : "pf-chip-static--ok"}`}
          data-testid="org-stability"
        >
          {c.localFallbacks > 0
            ? t("org.fallbacks", { n: c.localFallbacks })
            : t("org.noInterruptions")}
        </span>
      </td>
    </tr>
  );
}

/** 브라우저 내려받기(내려받기 링크를 잠깐 만들어 누른다) — URL API 가 없으면(시험 환경) 아무것도 하지 않는다 */
function download(blob: Blob, name: string) {
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
